import { useTranslation } from 'react-i18next';
import type { GetServerSideProps } from 'next';
import { Page } from '@web/components/page';
import {
  BreedAdvisorGuide,
  type BreedAdvisorGuideProps,
} from '@web/components/breed-advisor-guide';
import {
  CreateBreedMatchScreen,
  CreateBreedMatchScreenProps,
} from 'shared/screens/breed-match/create-breed-match';
import { withServerProps } from '@web/library/with-server-props';
import { PageBuilder } from 'shared/library/page-builder';

type DogBreedAdvisorProps = CreateBreedMatchScreenProps & {
  breedAdvisorGuide: BreedAdvisorGuideProps | null;
};

const pageBuilder = new PageBuilder<CreateBreedMatchScreenProps>().translationPaths([
  'documents.breed_advisor_guide',
]);

export default function DogBreedAdvisor(props: DogBreedAdvisorProps) {
  const { t } = useTranslation('screens');

  return (
    <Page
      {...props}
      register={(seo) => {
        if (props.breedAdvisorGuide) {
          return seo.forPage(
            t('breed_match.create_breed_match.title'),
            t('breed_match.create_breed_match.description'),
            'https://getapet.com/images/breed-advisor/guide/golden-retriever.webp',
          );
        }

        return seo
          .setTitle(t('breed_match.create_breed_match.title'))
          .setDescription(t('breed_match.create_breed_match.description'));
      }}
    >
      <div id="hundrasexperten" className="scroll-mt-20">
        <CreateBreedMatchScreen {...props} />
      </div>
      {props.breedAdvisorGuide && <BreedAdvisorGuide {...props.breedAdvisorGuide} />}
    </Page>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const result = await withServerProps(pageBuilder)(context);
  if (!('props' in result)) return result;

  const props = await result.props;
  // The guide is written in Swedish and Finnish; other languages show only the advisor.
  const languageCode = props.defaultProps.languageCode;
  const markdown = props.defaultProps.translations[languageCode]?.documents?.breed_advisor_guide;

  if (typeof markdown !== 'string' || !markdown.trim()) {
    return { props: { ...props, breedAdvisorGuide: null } };
  }

  const { compileMarkdown } = await import('shared/library/compile-markdown.server');
  const compiledTree = compileMarkdown(markdown);
  const translations = {
    ...props.defaultProps.translations,
    [languageCode]: {
      ...props.defaultProps.translations[languageCode],
      documents: { ...props.defaultProps.translations[languageCode].documents },
    },
  };
  delete translations[languageCode].documents.breed_advisor_guide;

  return {
    props: {
      ...props,
      defaultProps: { ...props.defaultProps, translations },
      breedAdvisorGuide: { compiledTree },
    },
  };
};
